/**
 * Shared chrome for the trader and IB portals, in the reference app's format:
 * a pinned 16rem sidebar on desktop that becomes a spring slide-in drawer on
 * mobile, and a slim header carrying a "RDFX / Page" breadcrumb.
 *
 * Both portals render the same shell so navigation never changes shape when a
 * trader crosses into their partner area. Callers supply the nav; auth, module
 * filtering and the real header actions stay on our side.
 */
import { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, ExternalLink, LogOut, Menu, X } from "lucide-react";
import { Toaster } from "sonner";
import { useLogout } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { NotificationBell, ThemeToggle } from "./Topbar";
import type { RdfxNavItem, RdfxNavSection } from "./rdfxNav";

export function initialsOf(name: string) {
  return name.trim().split(/\s+/).map((n) => n[0]).join("").slice(0, 2).toUpperCase() || "T";
}

export interface PortalShellProps {
  mainNav: RdfxNavItem[];
  sections: RdfxNavSection[];
  /** Routes the tenant has enabled. Items outside this set are hidden. */
  allowed?: Set<string>;
  pageLabels: Record<string, string>;
  name: string;
  roleLabel: string;
  profileHref: string;
  /** Optional per-item rewrite (e.g. "Become an IB" → the IB portal). */
  resolveItem?: (item: RdfxNavItem) => RdfxNavItem;
  /** Right-hand status pill in the header. */
  statusPill?: React.ReactNode;
}

function Sidebar({
  onClose, mainNav, sections, allowed, name, roleLabel, profileHref, resolveItem,
}: PortalShellProps & { onClose?: () => void }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  // External entries are not routes, so the enabled-route list has nothing to
  // say about them — they'd otherwise be filtered out for every trader.
  const visible = (i: RdfxNavItem) => Boolean(i.href) || !allowed || allowed.has(i.to);
  const resolve = resolveItem ?? ((i: RdfxNavItem) => i);

  return (
    <div className="flex h-full flex-col border-r border-border bg-card">
      <div className="flex shrink-0 items-center justify-between px-5 py-5">
        <div className="flex items-center gap-3">
          <img
            src="/rdfx-tile.png?v=2"
            alt="RDFX"
            className="h-9 w-9 shrink-0 rounded-xl object-cover"
          />
          <div>
            <p className="text-sm font-bold leading-tight text-foreground">RDFX</p>
            <p className="text-[10px] leading-tight text-muted-foreground">Trade Platform</p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} aria-label="Close menu" className="rounded-lg p-1.5 hover:bg-accent lg:hidden">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
      </div>

      <div className="px-3 pb-2">
        {mainNav.filter(visible).map((raw) => {
          const item = resolve(raw);
          const active = pathname === item.to;
          const Icon = item.icon;
          return (
            <Link
              key={raw.to}
              to={item.to}
              onClick={onClose}
              className={cn(
                "group mb-0.5 flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all",
                active ? "bg-accent/60 text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <div className={cn(
                "grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors",
                active ? "bg-primary text-primary-foreground" : "bg-muted group-hover:bg-muted/80",
              )}>
                <Icon className="h-4 w-4" />
              </div>
              <span className="text-sm font-semibold">{item.label}</span>
              {active && <ChevronRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />}
            </Link>
          );
        })}
      </div>

      <div className="mx-4 my-1 border-t border-border" />

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-3 py-2">
        {sections.map((section) => {
          const items = section.items.filter(visible);
          if (!items.length) return null;
          return (
            <div key={section.title}>
              <p className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                {section.title}
              </p>
              {items.map((raw) => {
                const item = resolve(raw);
                const Icon = item.icon;
                if (item.href) {
                  // Leaves the app, so it is a plain anchor — noopener because
                  // the target would otherwise get a handle on this window.
                  return (
                    <a
                      key={raw.to}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={onClose}
                      className="group mb-0.5 flex items-center gap-3 rounded-xl px-3 py-2 text-muted-foreground transition-all hover:bg-muted hover:text-foreground"
                    >
                      <div className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-muted">
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <span className="text-sm font-medium">{item.label}</span>
                      <ExternalLink className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50" />
                    </a>
                  );
                }
                return (
                  <NavLink
                    key={raw.to}
                    to={item.to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      cn(
                        "group mb-0.5 flex items-center gap-3 rounded-xl px-3 py-2 transition-all",
                        isActive ? "bg-accent/60 text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <div className={cn(
                          "grid h-7 w-7 shrink-0 place-items-center rounded-lg",
                          isActive ? "bg-primary text-primary-foreground" : "bg-muted",
                        )}>
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <span className={cn("text-sm", isActive ? "font-semibold text-foreground" : "font-medium")}>
                          {item.label}
                        </span>
                        {isActive && <div className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="mt-2 shrink-0 border-t border-border px-3 pb-4 pt-2">
        <div className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-muted">
          <Link to={profileHref} onClick={onClose} className="flex min-w-0 flex-1 items-center gap-3">
            <div className="relative shrink-0">
              <div className="grid h-9 w-9 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                {initialsOf(name)}
              </div>
              <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-card bg-success" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">{name}</p>
              <p className="text-xs text-muted-foreground">{roleLabel}</p>
            </div>
          </Link>
          <button
            type="button"
            // useLogout only clears the cache — every call site navigates itself.
            // Without this the cookie dropped but the screen stayed put, so the
            // button looked dead.
            onClick={() => {
              onClose?.();
              logout.mutate(undefined, {
                onSettled: () => navigate("/login", { replace: true }),
              });
            }}
            disabled={logout.isPending}
            aria-label="Sign out"
            className="shrink-0 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function PortalShell(props: PortalShellProps) {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const pageLabel = props.pageLabels[pathname] ?? "RDFX";

  return (
    <div className="theme-rdfx flex h-screen overflow-hidden bg-background">
      <aside className="hidden h-full w-64 shrink-0 flex-col lg:flex">
        <Sidebar {...props} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="z-30 flex h-14 shrink-0 items-center justify-between border-b border-border bg-card px-5">
          <div className="flex items-center gap-3">
            <button
              className="rounded-lg p-1.5 transition-colors hover:bg-accent lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5 text-foreground" />
            </button>
            <div className="flex items-center gap-1.5 text-sm">
              <span className="font-semibold text-primary">RDFX</span>
              <span className="text-muted-foreground">/</span>
              <span className="font-semibold text-foreground">{pageLabel}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {props.statusPill}
            <ThemeToggle />
            <NotificationBell />
            <Link
              to={props.profileHref}
              className="hidden h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground transition-opacity hover:opacity-80 lg:flex"
            >
              {initialsOf(props.name)}
            </Link>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-background">
          <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
            <Outlet />
          </div>
        </main>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/50 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="fixed left-0 top-0 z-50 h-full w-72 shadow-2xl lg:hidden"
            >
              <Sidebar {...props} onClose={() => setMobileOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}
